import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  Button,
  Container,
  Grid,
  Header,
  Icon,
  Segment,
  Form,
  Input,
  Dropdown,
  Checkbox,
} from "semantic-ui-react";
import { Link } from "react-router-dom";
import { BackOfficeRoutes } from "@routes/urls";

const SEPARATOR_OPTIONS = [
  {
    key: "newline",
    text: "New Line (Each part on separate line)",
    value: "newline",
  },
  { key: "dash", text: "Dash (-)", value: "dash" },
  { key: "plus", text: "Plus (+)", value: "plus" },
  { key: "space", text: "Space ( )", value: "space" },
  { key: "custom", text: "Custom Separator", value: "custom" },
];

const SHAPE_OPTIONS = [
  { key: "circle", text: "Circle", value: "circle" },
  { key: "rectangle", text: "Rectangle (3:4 ratio)", value: "rectangle" },
];

const LABELS_PER_ROW_OPTIONS = [
  { key: 1, text: "1 per row (One label per line)", value: 1 },
  { key: 2, text: "2 per row", value: 2 },
  { key: 3, text: "3 per row (Default)", value: 3 },
  { key: 4, text: "4 per row", value: 4 },
  { key: 5, text: "5 per row", value: 5 },
  { key: 6, text: "6 per row (Maximum)", value: 6 },
];

const DocumentLabelsFormatComponent = ({ selectedDocuments }) => {
  const [labelSize, setLabelSize] = useState(150);
  const [labelShape, setLabelShape] = useState("circle");
  const [formatOptions, setFormatOptions] = useState({
    separatorType: "newline",
    customSeparator: "",
    lineBreakAfter: [],
    enableCustomLineBreaks: false,
  });
  const [layoutOptions, setLayoutOptions] = useState({
    labelsPerRow: 3,
    horizontalSpacing: 10,
    verticalSpacing: 30,
    fontSize: 14,
    lineHeight: 1.2,
  });

  useEffect(() => {
    loadSavedOptions();
  }, [selectedDocuments]);

  const loadSavedOptions = () => {
    try {
      const savedFormatOptions = localStorage.getItem(
        "documentLabelFormatOptions"
      );
      if (savedFormatOptions) {
        setFormatOptions(JSON.parse(savedFormatOptions));
      }

      const savedLayoutOptions = localStorage.getItem(
        "documentLabelLayoutOptions"
      );
      if (savedLayoutOptions) {
        setLayoutOptions(JSON.parse(savedLayoutOptions));
      }

      const savedSize =
        localStorage.getItem("documentLabelSize") ||
        localStorage.getItem("documentLabelCircleSize");
      if (savedSize) {
        setLabelSize(parseInt(savedSize, 10));
        if (localStorage.getItem("documentLabelCircleSize")) {
          localStorage.setItem("documentLabelSize", savedSize);
          localStorage.removeItem("documentLabelCircleSize");
        }
      }

      const savedShape = localStorage.getItem("documentLabelShape");
      if (savedShape) {
        setLabelShape(savedShape);
      }
    } catch (error) {
      // Silent error handling for localStorage issues
    }
  };

  const formatCallNumber = (callNumber, options = formatOptions) => {
    if (callNumber === "N/A") return ["N/A"];

    let parts = callNumber.split(" ").filter((part) => part.trim() !== "");

    parts = parts.filter((part, index) => {
      if (
        part === "-" &&
        index < parts.length - 1 &&
        parts[index + 1].match(/^c\d+$/i)
      ) {
        return false;
      }
      return true;
    });

    if (!options.enableCustomLineBreaks) {
      switch (options.separatorType) {
        case "newline":
          return parts;
        case "dash":
          return [parts.join("-")];
        case "plus":
          return [parts.join("+")];
        case "space":
          return [parts.join(" ")];
        case "custom":
          return [parts.join(options.customSeparator || "-")];
        default:
          return parts;
      }
    } else {
      const result = [];
      let currentLine = [];

      parts.forEach((part, index) => {
        currentLine.push(part);
        if (
          options.lineBreakAfter.includes(index) ||
          index === parts.length - 1
        ) {
          const separator =
            options.separatorType === "custom"
              ? options.customSeparator || "-"
              : { dash: "-", plus: "+", space: " " }[options.separatorType] ||
                " ";
          result.push(currentLine.join(separator));
          currentLine = [];
        }
      });

      return result.length > 0 ? result : parts;
    }
  };

  const getLabelDimensions = () => {
    if (labelShape === "circle") {
      return { width: labelSize, height: labelSize, borderRadius: "50%" };
    } else {
      const width = labelSize;
      const height = (labelSize * 4) / 3;
      return { width, height, borderRadius: "8px" };
    }
  };

  const handleSizeChange = (value) => {
    setLabelSize(value);
    localStorage.setItem("documentLabelSize", value.toString());
  };

  const handleShapeChange = (shape) => {
    setLabelShape(shape);
    localStorage.setItem("documentLabelShape", shape);
  };

  const handleFormatOptionChange = (key, value) => {
    const newOptions = { ...formatOptions, [key]: value };
    setFormatOptions(newOptions);

    const cleanOptions = {
      separatorType: newOptions.separatorType,
      customSeparator: newOptions.customSeparator || "",
      lineBreakAfter: newOptions.lineBreakAfter || [],
      enableCustomLineBreaks: newOptions.enableCustomLineBreaks || false,
    };

    try {
      localStorage.setItem(
        "documentLabelFormatOptions",
        JSON.stringify(cleanOptions)
      );
    } catch (error) {
      // Silent error handling
    }
  };

  const handleLayoutOptionChange = (key, value) => {
    const newOptions = { ...layoutOptions, [key]: value };
    setLayoutOptions(newOptions);

    try {
      localStorage.setItem(
        "documentLabelLayoutOptions",
        JSON.stringify(newOptions)
      );
    } catch (error) {
      // Silent error handling
    }
  };

  const handleLineBreakToggle = (partIndex) => {
    const newLineBreakAfter = formatOptions.lineBreakAfter.includes(partIndex)
      ? formatOptions.lineBreakAfter.filter((i) => i !== partIndex)
      : [...formatOptions.lineBreakAfter, partIndex].sort((a, b) => a - b);

    handleFormatOptionChange("lineBreakAfter", newLineBreakAfter);
  };

  const getSizeLabel = () => {
    return labelShape === "circle"
      ? `Circle Diameter: ${labelSize}px`
      : `Rectangle Width: ${labelSize}px (Height: ${Math.round(
          (labelSize * 4) / 3
        )}px)`;
  };

  const renderPreview = () => {
    const callNumberParts = formatCallNumber("FR 2342 25352 23 c1");
    const dimensions = getLabelDimensions();

    return (
      <Segment basic textAlign="center">
        <div className="preview-area">
          <div
            className={`call-number-label call-number-${labelShape}`}
            style={{
              width: `${dimensions.width}px`,
              height: `${dimensions.height}px`,
              borderRadius: dimensions.borderRadius,
              border: "2px solid #000",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 auto",
              padding: "10px",
              boxSizing: "border-box",
              fontSize: `${layoutOptions.fontSize}px`,
              lineHeight: layoutOptions.lineHeight,
            }}
          >
            {callNumberParts.map((part, index) => (
              <div key={index} className="call-number-part">
                {part}
              </div>
            ))}
          </div>
        </div>
        <div className="preview-info">
          <Header as="h4">Sample Document Label</Header>
          <p>Original Call Number: FR 2342 25352 23 c1</p>
          <p>Formatted: {callNumberParts.join(" | ")}</p>
          <p>
            Font Size: {layoutOptions.fontSize}px | Shape:{" "}
            {labelShape === "circle" ? "Circle" : "Rectangle"} | Size:{" "}
            {labelSize}px{" "}
            {labelShape === "rectangle" &&
              `(${labelSize}×${Math.round((labelSize * 4) / 3)}px)`}{" "}
            | Line Height: {layoutOptions.lineHeight}
          </p>
        </div>
      </Segment>
    );
  };

  const renderCustomLineBreakControls = () => {
    if (!formatOptions.enableCustomLineBreaks) return null;

    const parts = "FR 2342 25352 23 c1"
      .split(" ")
      .filter((part) => part.trim() !== "");

    return (
      <Form.Field>
        <label>Line Breaks (check to add line break after part):</label>
        <div style={{ marginTop: "10px" }}>
          {parts.map((part, index) => (
            <div key={index} style={{ marginBottom: "5px" }}>
              <Checkbox
                label={`After "${part}" (Part ${index + 1})`}
                checked={formatOptions.lineBreakAfter.includes(index)}
                onChange={() => handleLineBreakToggle(index)}
              />
            </div>
          ))}
        </div>
      </Form.Field>
    );
  };

  return (
    <>
      <Header as="h2">Format Document Labels</Header>
      <Container fluid>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <Button
                as={Link}
                to={BackOfficeRoutes.documentLabelsSelected}
                icon
                labelPosition="left"
              >
                <Icon name="arrow left" />
                Back to Selected Documents
              </Button>
            </Grid.Column>
          </Grid.Row>

          {selectedDocuments.length === 0 ? (
            <Grid.Row>
              <Grid.Column width={16}>
                <Segment placeholder textAlign="center">
                  <Header icon>
                    <Icon name="tags" />
                    No documents selected for label generation
                  </Header>
                  <Button
                    as={Link}
                    to={BackOfficeRoutes.documentLabels}
                    primary
                  >
                    Select Documents
                  </Button>
                </Segment>
              </Grid.Column>
            </Grid.Row>
          ) : (
            <>
              <Grid.Row>
                <Grid.Column width={6}>
                  <Segment>
                    <Header as="h3">Label Settings</Header>
                    <Form>
                      <Form.Field>
                        <label>Label Shape</label>
                        <Dropdown
                          selection
                          options={SHAPE_OPTIONS}
                          value={labelShape}
                          onChange={(e, { value }) => handleShapeChange(value)}
                          style={{ width: "100%" }}
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>{getSizeLabel()}</label>
                        <Input
                          type="range"
                          min={50}
                          max={250}
                          step={10}
                          value={labelSize}
                          onChange={(e) =>
                            handleSizeChange(parseInt(e.target.value, 10))
                          }
                          style={{ width: "100%" }}
                        />
                        <div style={{ textAlign: "center", marginTop: "5px" }}>
                          {labelShape === "circle"
                            ? `${labelSize}px diameter`
                            : `${labelSize}×${Math.round(
                                (labelSize * 4) / 3
                              )}px`}
                        </div>
                      </Form.Field>

                      <Form.Field>
                        <label>Font Size: {layoutOptions.fontSize}px</label>
                        <Input
                          type="range"
                          min={8}
                          max={32}
                          step={1}
                          value={layoutOptions.fontSize}
                          onChange={(e) =>
                            handleLayoutOptionChange(
                              "fontSize",
                              parseInt(e.target.value, 10)
                            )
                          }
                          style={{ width: "100%" }}
                        />
                        <div style={{ textAlign: "center", marginTop: "5px" }}>
                          {layoutOptions.fontSize}px
                        </div>
                      </Form.Field>

                      <Form.Field>
                        <label>Line Spacing: {layoutOptions.lineHeight}</label>
                        <Input
                          type="range"
                          min={0.8}
                          max={2.0}
                          step={0.1}
                          value={layoutOptions.lineHeight}
                          onChange={(e) =>
                            handleLayoutOptionChange(
                              "lineHeight",
                              parseFloat(e.target.value)
                            )
                          }
                          style={{ width: "100%" }}
                        />
                        <div style={{ textAlign: "center", marginTop: "5px" }}>
                          {layoutOptions.lineHeight}x
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#666",
                            textAlign: "center",
                            marginTop: "2px",
                          }}
                        >
                          Controls vertical spacing between call number parts
                        </div>
                      </Form.Field>

                      <Form.Field>
                        <label>Call Number Format</label>
                        <Dropdown
                          selection
                          options={SEPARATOR_OPTIONS}
                          value={formatOptions.separatorType}
                          onChange={(e, { value }) =>
                            handleFormatOptionChange("separatorType", value)
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Field>

                      {formatOptions.separatorType === "custom" && (
                        <Form.Field>
                          <label>Custom Separator</label>
                          <Input
                            placeholder="Enter custom separator (e.g., -, +, |)"
                            value={formatOptions.customSeparator}
                            onChange={(e) =>
                              handleFormatOptionChange(
                                "customSeparator",
                                e.target.value
                              )
                            }
                          />
                        </Form.Field>
                      )}

                      <Form.Field>
                        <Checkbox
                          label="Enable Custom Line Breaks"
                          checked={formatOptions.enableCustomLineBreaks}
                          onChange={(e, { checked }) =>
                            handleFormatOptionChange(
                              "enableCustomLineBreaks",
                              checked
                            )
                          }
                        />
                      </Form.Field>

                      {renderCustomLineBreakControls()}
                    </Form>
                  </Segment>

                  <Segment>
                    <Header as="h3">Print Layout</Header>
                    <Form>
                      <Form.Field>
                        <label>Labels Per Row</label>
                        <Dropdown
                          selection
                          options={LABELS_PER_ROW_OPTIONS}
                          value={layoutOptions.labelsPerRow}
                          onChange={(e, { value }) =>
                            handleLayoutOptionChange("labelsPerRow", value)
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>
                          Horizontal Spacing: {layoutOptions.horizontalSpacing}
                          px
                        </label>
                        <Input
                          type="range"
                          min={0}
                          max={50}
                          step={5}
                          value={layoutOptions.horizontalSpacing}
                          onChange={(e) =>
                            handleLayoutOptionChange(
                              "horizontalSpacing",
                              parseInt(e.target.value, 10)
                            )
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Field>

                      <Form.Field>
                        <label>
                          Vertical Spacing: {layoutOptions.verticalSpacing}px
                        </label>
                        <Input
                          type="range"
                          min={10}
                          max={100}
                          step={10}
                          value={layoutOptions.verticalSpacing}
                          onChange={(e) =>
                            handleLayoutOptionChange(
                              "verticalSpacing",
                              parseInt(e.target.value, 10)
                            )
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Field>
                    </Form>
                  </Segment>
                </Grid.Column>

                <Grid.Column width={10}>
                  <Segment>
                    <Header as="h3">Label Preview</Header>
                    {renderPreview()}
                  </Segment>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <Button
                    as={Link}
                    to={BackOfficeRoutes.documentLabelsSelected}
                    color="green"
                    icon
                    labelPosition="left"
                    floated="right"
                    size="large"
                  >
                    <Icon name="save" />
                    Save Format & Return
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </>
          )}
        </Grid>
      </Container>
    </>
  );
};

DocumentLabelsFormatComponent.propTypes = {
  selectedDocuments: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  selectedDocuments: state.documentLabels?.selectedDocuments || [],
});

export const DocumentLabelsFormat = connect(mapStateToProps)(
  DocumentLabelsFormatComponent
);
